import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { 
  Users, Search, Plus, Filter, ChevronRight, Phone, Mail,
  Calendar, FileText, Video, MessageSquare, MoreVertical,
  Brain, Shield, Clock, AlertCircle, CheckCircle, Heart
} from 'lucide-react';

const BehavioralHealthClients = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const clients = [
    { 
      id: 1, 
      name: 'Sarah Johnson', 
      email: 'sarah.j@email.com',
      phone: '(555) 123-4567',
      dob: '03/15/1985',
      status: 'active',
      diagnosis: 'Generalized Anxiety Disorder',
      lastSession: '02/22/2026',
      nextSession: '02/24/2026 9:00 AM',
      sessionsCompleted: 12,
      insuranceProvider: 'Blue Cross Blue Shield'
    },
    { 
      id: 2, 
      name: 'Michael Chen', 
      email: 'mchen@email.com',
      phone: '(555) 234-5678',
      dob: '07/22/1990',
      status: 'active',
      diagnosis: 'Major Depressive Disorder',
      lastSession: '02/17/2026',
      nextSession: '02/24/2026 10:30 AM',
      sessionsCompleted: 8,
      insuranceProvider: 'Aetna'
    },
    { 
      id: 3, 
      name: 'Emily Rodriguez', 
      email: 'emily.r@email.com',
      phone: '(555) 345-6789',
      dob: '11/30/1978',
      status: 'active',
      diagnosis: 'PTSD',
      lastSession: '02/21/2026',
      nextSession: '02/24/2026 1:00 PM',
      sessionsCompleted: 15,
      insuranceProvider: 'United Healthcare'
    },
    { 
      id: 4, 
      name: 'David Thompson', 
      email: 'dthompson@email.com',
      phone: '(555) 456-7890',
      dob: '05/08/1995',
      status: 'new',
      diagnosis: 'Social Anxiety',
      lastSession: 'N/A',
      nextSession: '02/24/2026 3:00 PM',
      sessionsCompleted: 0,
      insuranceProvider: 'Cigna'
    },
    { 
      id: 5, 
      name: 'Jennifer Williams', 
      email: 'jwilliams@email.com',
      phone: '(555) 567-8901',
      dob: '09/12/1982',
      status: 'inactive',
      diagnosis: 'Bipolar II Disorder',
      lastSession: '01/15/2026',
      nextSession: 'Not scheduled',
      sessionsCompleted: 24,
      insuranceProvider: 'Humana'
    },
    { 
      id: 6, 
      name: 'Robert Garcia', 
      email: 'rgarcia@email.com',
      phone: '(555) 678-9012',
      dob: '02/28/1988',
      status: 'active',
      diagnosis: 'OCD',
      lastSession: '02/20/2026',
      nextSession: '02/27/2026 11:00 AM',
      sessionsCompleted: 6,
      insuranceProvider: 'Kaiser Permanente'
    },
  ];

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || client.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'inactive': return 'bg-gray-100 text-gray-700';
      case 'new': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Users className="w-8 h-8 text-orange-500" />
                Client Management
              </h1>
              <p className="text-gray-500 mt-1">Manage your behavioral health clients</p>
            </div>
            <Link 
              to="/dashboard/behavioral-health/clients/new"
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <Plus className="w-5 h-5" />
              New Client
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{clients.length}</h3>
                <p className="text-gray-500 text-sm">Total Clients</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{clients.filter(c => c.status === 'active').length}</h3>
                <p className="text-gray-500 text-sm">Active Clients</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center gap-4">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">4</h3>
                <p className="text-gray-500 text-sm">Sessions Today</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center gap-4">
              <div className="bg-orange-100 p-3 rounded-lg">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{clients.filter(c => c.status === 'new').length}</h3>
                <p className="text-gray-500 text-sm">New Intakes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-xl shadow mb-6">
          <div className="p-4 flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search clients by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="new">New</option>
              </select>
            </div>
          </div>
        </div>

        {/* Client List */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Client</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Diagnosis</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Next Session</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Sessions</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr key={client.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">
                        {client.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{client.name}</h4>
                        <p className="text-sm text-gray-500">{client.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-700">{client.diagnosis}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-700">{client.nextSession}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-700">{client.sessionsCompleted}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(client.status)}`}>
                      {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Link 
                        to="/dashboard/behavioral-health/telehealth"
                        className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                        title="Start Session"
                      >
                        <Video className="w-4 h-4" />
                      </Link>
                      <Link 
                        to="/dashboard/behavioral-health/messages"
                        className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors"
                        title="Message"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </Link>
                      <Link 
                        to={`/dashboard/behavioral-health/clients/${client.id}`}
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                        title="View Profile"
                      >
                        <FileText className="w-4 h-4" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BehavioralHealthClients;
